const gaussianVertexShader = /* glsl */ `
precision highp float;

attribute float gaussianIndex;

uniform sampler2D gaussianCenterOpacityTexture;
uniform sampler2D gaussianScaleTexture;
uniform sampler2D gaussianRotationTexture;
uniform sampler2D gaussianColorTexture;

uniform float gaussianTextureWidth;
uniform float gaussianTextureHeight;
uniform vec2 viewport;
uniform float globalScale;

varying vec2 vGaussianPosition;
varying vec3 vColor;
varying float vOpacity;

const float MIN_DEPTH = 0.1;
const float MIN_VARIANCE = 0.0001;
const float EIGEN_EPSILON = 0.00001;
const float LOW_PASS_VARIANCE = 0.3;
const float SIGMA_EXTENT = 3.0;
const float MAX_SPLAT_RADIUS_PX = 48.0;

vec2 gaussianTextureUV(float index) {
    float x = mod(index, gaussianTextureWidth);
    float y = floor(index / gaussianTextureWidth);

    return vec2(
        (x + 0.5) / gaussianTextureWidth,
        (y + 0.5) / gaussianTextureHeight
    );
}

mat3 quaternionToMatrix(vec4 qRaw) {
    vec4 q = normalize(qRaw);

    // Stored quaternion convention: (w, x, y, z).
    float w = q.x;
    float x = q.y;
    float y = q.z;
    float z = q.w;

    return mat3(
        1.0 - 2.0 * y * y - 2.0 * z * z,
        2.0 * x * y + 2.0 * w * z,
        2.0 * x * z - 2.0 * w * y,

        2.0 * x * y - 2.0 * w * z,
        1.0 - 2.0 * x * x - 2.0 * z * z,
        2.0 * y * z + 2.0 * w * x,

        2.0 * x * z + 2.0 * w * y,
        2.0 * y * z - 2.0 * w * x,
        1.0 - 2.0 * x * x - 2.0 * y * y
    );
}

void main() {
    vec2 gaussianUV = gaussianTextureUV(gaussianIndex);

    vec4 centerOpacity = texture2D(
        gaussianCenterOpacityTexture,
        gaussianUV
    );

    vec3 center = centerOpacity.xyz;
    float opacity = centerOpacity.w;

    vec3 scale = texture2D(
        gaussianScaleTexture,
        gaussianUV
    ).xyz * globalScale;

    vec4 rotation = texture2D(
        gaussianRotationTexture,
        gaussianUV
    );

    vec3 color = texture2D(
        gaussianColorTexture,
        gaussianUV
    ).xyz;

    vec4 cameraCenter = modelViewMatrix * vec4(center, 1.0);
    float depth = -cameraCenter.z;

    if (depth <= MIN_DEPTH) {
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        vGaussianPosition = vec2(10.0);
        vColor = color;
        vOpacity = 0.0;
        return;
    }

    // 3D covariance: Σ = R S² Rᵀ.
    mat3 R = quaternionToMatrix(rotation);

    mat3 S2 = mat3(
        scale.x * scale.x, 0.0, 0.0,
        0.0, scale.y * scale.y, 0.0,
        0.0, 0.0, scale.z * scale.z
    );

    mat3 covarianceWorld = R * S2 * transpose(R);

    mat3 viewRotation = mat3(modelViewMatrix);
    mat3 covarianceCamera =
        viewRotation *
        covarianceWorld *
        transpose(viewRotation);

    // Perspective Jacobian in pixel coordinates.
    float fx = projectionMatrix[0][0] * viewport.x * 0.5;
    float fy = projectionMatrix[1][1] * viewport.y * 0.5;

    float x = cameraCenter.x;
    float y = cameraCenter.y;
    float depthSquared = depth * depth;

    vec3 j0 = vec3(
        fx / depth,
        0.0,
        -fx * x / depthSquared
    );

    vec3 j1 = vec3(
        0.0,
        fy / depth,
        -fy * y / depthSquared
    );

    float covXX = dot(j0, covarianceCamera * j0);
    float covXY = dot(j0, covarianceCamera * j1);
    float covYY = dot(j1, covarianceCamera * j1);

    // Minimum screen-space variance prevents sub-pixel splats vanishing.
    covXX += LOW_PASS_VARIANCE;
    covYY += LOW_PASS_VARIANCE;

    // Eigen-decompose the symmetric 2D covariance.
    float trace = covXX + covYY;
    float determinant = covXX * covYY - covXY * covXY;

    float discriminant = sqrt(
        max(
            trace * trace * 0.25 - determinant,
            0.0
        )
    );

    float lambda1 = max(
        trace * 0.5 + discriminant,
        MIN_VARIANCE
    );

    float lambda2 = max(
        trace * 0.5 - discriminant,
        MIN_VARIANCE
    );

    vec2 axis1;

    if (abs(covXY) > EIGEN_EPSILON) {
        axis1 = normalize(
            vec2(lambda1 - covYY, covXY)
        );
    } else {
        axis1 = covXX >= covYY
            ? vec2(1.0, 0.0)
            : vec2(0.0, 1.0);
    }

    vec2 axis2 = vec2(-axis1.y, axis1.x);

    vec2 radius1 =
        axis1 * sqrt(lambda1) * SIGMA_EXTENT;

    vec2 radius2 =
        axis2 * sqrt(lambda2) * SIGMA_EXTENT;

    // Limit pathological near-camera splats.
    float maxRadius = max(length(radius1), length(radius2));

    if (maxRadius > MAX_SPLAT_RADIUS_PX) {
        float scaleFactor = MAX_SPLAT_RADIUS_PX / maxRadius;
        radius1 *= scaleFactor;
        radius2 *= scaleFactor;
    }

    vec4 clipCenter = projectionMatrix * cameraCenter;

    vec2 pixelOffset =
        position.x * radius1 +
        position.y * radius2;

    vec2 ndcOffset = vec2(
        pixelOffset.x * 2.0 / viewport.x,
        pixelOffset.y * 2.0 / viewport.y
    );

    gl_Position = clipCenter;
    gl_Position.xy += ndcOffset * clipCenter.w;

    vGaussianPosition = position.xy * SIGMA_EXTENT;
    vColor = color;
    vOpacity = opacity;
}
`;

export default gaussianVertexShader;
